import React, { useState, useEffect, useCallback } from "react";
import { FiPlus, FiEdit2, FiTrash2 } from "react-icons/fi";
import toast from "react-hot-toast";
import Header from "../../../../components/layout/Header/Header";
import Sidebar from "../../../../components/layout/Sidebar/Sidebar";
import Table from "../../../../components/common/Table/Table";
import Button from "../../../../components/common/Button/Button";
import Modal from "../../../../components/common/Modal/Modal";
import Loading from "../../../../components/common/Loading/Loading";
import axios from "axios";
import { formatCurrency } from "../../../../utils/helpers";
import "../../UserManagement/UserList/UserList.css";

const PricingConfig = () => {
  const backendUrl = process.env.REACT_APP_BACKEND_IP || "http://localhost:5000";
  const modalGridStyle = {
    display: "grid",
    gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
    gap: "16px",
    alignItems: "start",
  };
  const fullWidthStyle = { gridColumn: "1 / -1" };
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [services, setServices] = useState([]);
  const [categories, setCategories] = useState([]);
  const [subCategories, setSubCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editService, setEditService] = useState(null);
  const [formData, setFormData] = useState({
    name: "", description: "", category: "", subCategory: "", price: "", pricingType: "Fixed", duration: "", discount: 0, image1: null, image2: null,
  });

  // Derived: top-level categories and sub-categories filtered by selected category
  const topLevelCategories = categories;
  const subCategoriesForSelected = subCategories.filter(
    (sc) => sc.category && (sc.category._id || sc.category) === formData.category
  );

  const fetchData = useCallback(async () => {
    try {
      const [servRes, catRes, subCatRes] = await Promise.all([
        axios.get(`${backendUrl}/api/services`),
        axios.get(`${backendUrl}/api/categories`),
        axios.get(`${backendUrl}/api/subcategories`),
      ]);
      setServices(servRes.data);
      setCategories(catRes.data);
      setSubCategories(subCatRes.data);
    } catch (error) {
      toast.error("Failed to load data");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.category || !formData.price || !formData.duration) {
      toast.error("Fill all required fields");
      return;
    }
    try {
      const data = new FormData();
      data.append('name', formData.name);
      data.append('description', formData.description);
      data.append('category', formData.category);
      data.append('subCategory', formData.subCategory);
      data.append('price', formData.price);
      data.append('servicePercentage', formData.servicePercentage);
      data.append('pricingType', formData.pricingType);
      data.append('duration', formData.duration);
      data.append('discount', formData.discount);
      if (formData.image1) {
        data.append('images', formData.image1);
      }
      if (formData.image2) {
        data.append('images', formData.image2);
      }

      const config = {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      };

      if (editService) {
        await axios.put(`${backendUrl}/api/services/${editService._id}`, data, config);
        toast.success("Service updated");
      } else {
        await axios.post(`${backendUrl}/api/services`, data, config);
        toast.success("Service created");
      }
      setModalOpen(false);
      setEditService(null);
      setFormData({ name: "", description: "", category: "", subCategory: "", price: "", pricingType: "Fixed", duration: "", discount: 0, image1: null, image2: null });
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed");
    }
  };

  const handleEdit = (svc) => {
    setEditService(svc);
    setFormData({
      name: svc.name,
      description: svc.description || "",
      category: svc.category?._id || svc.category,
      subCategory: svc.subCategory?._id || svc.subCategory || "",
      price: svc.price,
      pricingType: svc.pricingType,
      duration: svc.duration,
      discount: svc.discount || 0,
      image1: null,
      image2: null,
      servicePercentage: svc.servicePercentage !== undefined ? svc.servicePercentage : "",
    });
    setModalOpen(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this service?")) return;
    try {
      await axios.delete(`${backendUrl}/api/services/${id}`);
      toast.success("Deleted");
      fetchData();
    } catch (err) {
      toast.error("Failed");
    }
  };

  const columns = [
    { key: "name", label: "Service Name" },
    { key: "category", label: "Category", render: (row) => row.category?.name || "-" },
    {
      key: "images",
      label: "Images",
      render: (row) => (
        <div style={{ display: "flex", gap: "8px" }}>
          {row.image1 && <img src={row.image1} alt="Image 1" style={{ width: "50px", height: "50px", borderRadius: "4px", objectFit: "cover" }} />}
          {row.image2 && <img src={row.image2} alt="Image 2" style={{ width: "50px", height: "50px", borderRadius: "4px", objectFit: "cover" }} />}
          {!row.image1 && !row.image2 && <span style={{ color: "#bdc3c7" }}>No images</span>}
        </div>
      )
    },
    { key: "price", label: "Price", render: (row) => formatCurrency(row.price) },
    { key: "pricingType", label: "Type" },
    { key: "duration", label: "Duration", render: (row) => `${row.duration} mins` },
    { key: "discount", label: "Discount", render: (row) => `${row.discount || 0}%` },
    {
      key: "actions", label: "Actions",
      render: (row) => (
        <div className="table-actions">
          <button className="action-btn edit" onClick={() => handleEdit(row)}><FiEdit2 /></button>
          <button className="action-btn danger" onClick={() => handleDelete(row._id)}><FiTrash2 /></button>
        </div>
      ),
    },
  ];

  return (
    <div className="dashboard-layout">
      <Header onToggleSidebar={() => setSidebarCollapsed(!sidebarCollapsed)} />
      <Sidebar collapsed={sidebarCollapsed} />
      <main className={`main-content ${sidebarCollapsed ? "sidebar-collapsed" : ""}`}>
        <div className="page-header">
          <div><h1>Services & Pricing</h1><p>Manage services and pricing</p></div>
          <Button onClick={() => { setEditService(null); setFormData({ name: "", description: "", category: "", subCategory: "", price: "", pricingType: "Fixed", duration: "", discount: 0, image1: null, image2: null }); setModalOpen(true); }}>
            <FiPlus /> Add Service
          </Button>
        </div>
        <div style={{ background: "#f8fcf9", border: "1px solid #dbeee0", borderRadius: "12px", padding: "14px 16px", marginBottom: "16px", color: "#315c45" }}>
          This page implements pricing model configuration. Admins can define fixed, hourly, or package pricing, set durations, and apply service-level discounts.
        </div>
        {loading ? <Loading /> : <Table columns={columns} data={services} emptyMessage="No services" />}
        <Modal isOpen={modalOpen} onClose={() => setModalOpen(false)} title={editService ? "Edit Service" : "Add Service"} size="large">
          <form onSubmit={handleSubmit}>
            <div style={modalGridStyle}>
              <div className="form-group">
                <label>Service Name</label>
                <input value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} placeholder="Enter service name" />
              </div>
              <div className="form-group">
                <label>Category</label>
                <select value={formData.category} onChange={(e) => setFormData({ ...formData, category: e.target.value, subCategory: "" })}>
                  <option value="">Select Category</option>
                  {topLevelCategories.map((c) => <option key={c._id} value={c._id}>{c.name}</option>)}
                </select>
              </div>
              <div className="form-group">
                <label>Sub Category</label>
                <select value={formData.subCategory} onChange={(e) => setFormData({ ...formData, subCategory: e.target.value })} disabled={!formData.category}>
                  <option value="">Select Sub Category (optional)</option>
                  {subCategoriesForSelected.map((c) => <option key={c._id} value={c._id}>{c.name}</option>)}
                </select>
              </div>
              <div className="form-group">
                <label>Pricing Type</label>
                <select value={formData.pricingType} onChange={(e) => setFormData({ ...formData, pricingType: e.target.value })}>
                  <option value="Fixed">Fixed</option>
                  <option value="Hourly">Hourly</option>
                  <option value="Package">Package</option>
                </select>
              </div>
              <div className="form-group">
                <label>Price (₹)</label>
                <input type="number" value={formData.price} onChange={(e) => setFormData({ ...formData, price: e.target.value })} placeholder="0.00" />
              </div>
              <div className="form-group">
                <label>Service Percentage (%)</label>
                <input
                  type="number"
                  min="0"
                  max="100"
                  value={formData.servicePercentage}
                  onChange={(e) => setFormData({ ...formData, servicePercentage: e.target.value })}
                  placeholder="e.g. 10 for 10%"
                />
              </div>
              <div className="form-group">
                <label>Duration (mins)</label>
                <input type="number" value={formData.duration} onChange={(e) => setFormData({ ...formData, duration: e.target.value })} placeholder="60" />
              </div>
              <div className="form-group">
                <label>Discount (%)</label>
                <input type="number" value={formData.discount} onChange={(e) => setFormData({ ...formData, discount: e.target.value })} min="0" max="100" placeholder="0" />
              </div>
              <div className="form-group" style={fullWidthStyle}>
                <label>Service Image 1</label>
                <input type="file" accept="image/*" onChange={(e) => setFormData({ ...formData, image1: e.target.files[0] })} />
              </div>
              <div className="form-group" style={fullWidthStyle}>
                <label>Service Image 2</label>
                <input type="file" accept="image/*" onChange={(e) => setFormData({ ...formData, image2: e.target.files[0] })} />
              </div>
              <div className="form-group" style={fullWidthStyle}>
                <label>Description</label>
                <textarea rows="4" value={formData.description} onChange={(e) => setFormData({ ...formData, description: e.target.value })} placeholder="Add a short service description" style={{ resize: "vertical" }} />
              </div>
            </div>
            <div className="form-actions">
              <Button variant="secondary" onClick={() => setModalOpen(false)}>Cancel</Button>
              <Button type="submit">{editService ? "Update" : "Create"}</Button>
            </div>
          </form>
        </Modal>
      </main>
    </div>
  );
};

export default PricingConfig;
